/**
 * Workstation bet-breakdown member multi-select — Lab `bet-breakdown.html` ui-select.
 *
 * Live: `POST /v1/member/searchHierarchyNew` (refresh on type, 100ms debounce).
 * Demo: filters usernames already present on the loaded bet list.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Spinner } from '@w7/ui'
import { hasLiveApi } from '@/api/client'
import { useSearchHierarchyNew } from '@/api/hooks/member/useSearchHierarchyNew'

/** Lab ui-select `delay="100"`. */
const DEBOUNCE_MS = 100

export interface WorkstationMemberSearchProps {
  selected: string[]
  draft: string
  onDraftChange: (next: string) => void
  onAdd: (name: string) => void
  onRemove: (name: string) => void
  onEnter: () => void
  /** Demo / offline suggestions from bets on the current book. */
  fixtureSuggestions: string[]
}

export function WorkstationMemberSearch({
  selected,
  draft,
  onDraftChange,
  onAdd,
  onRemove,
  onEnter,
  fixtureSuggestions,
}: WorkstationMemberSearchProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [debouncedDraft, setDebouncedDraft] = useState('')
  const hierarchyLive = hasLiveApi()

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedDraft(draft), DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [draft])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      const root = rootRef.current
      if (root && !root.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const hierarchySearch = useSearchHierarchyNew(debouncedDraft, open, {
    prefetchOnEmpty: true,
  })

  const suggestions = useMemo(() => {
    const excludeSelected = (names: string[]) =>
      names.filter((name) => !selected.includes(name))

    if (hierarchyLive) {
      return excludeSelected(hierarchySearch.data ?? [])
    }

    const needle = draft.trim().toLowerCase()
    return excludeSelected(
      fixtureSuggestions.filter((name) => !needle || name.toLowerCase().includes(needle)),
    )
  }, [hierarchyLive, hierarchySearch.data, draft, fixtureSuggestions, selected])

  const showPanel =
    open &&
    (hierarchySearch.isFetching || suggestions.length > 0 || (hierarchyLive && hierarchySearch.isError))

  const pick = (name: string) => {
    onAdd(name)
    onDraftChange('')
    setOpen(true)
  }

  return (
    <div className="w7-ws-bets-member" ref={rootRef}>
      <div
        className="w7-ws-bets-chips"
        onFocusCapture={() => setOpen(true)}
      >
        {selected.map((name) => (
          <button
            key={name}
            type="button"
            className="w7-ws-bets-chip"
            onClick={() => onRemove(name)}
            title="Remove"
          >
            {name} ×
          </button>
        ))}
        <input
          type="text"
          value={draft}
          placeholder="Enter or Select Member Name to Search"
          aria-expanded={showPanel}
          aria-controls="w7-ws-member-suggest-list"
          aria-autocomplete="list"
          aria-busy={hierarchyLive && hierarchySearch.isFetching}
          onChange={(e) => {
            onDraftChange(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onEnter()
            }
            if (e.key === 'Escape') setOpen(false)
          }}
        />
      </div>

      {showPanel ? (
        <ul
          id="w7-ws-member-suggest-list"
          className="w7-ws-member-suggest"
          role="listbox"
          aria-label="Member suggestions"
        >
          {hierarchyLive && hierarchySearch.isFetching && suggestions.length === 0 ? (
            <li className="w7-ws-member-suggest-status" role="presentation">
              <Spinner size={14} />
              <span>Searching downline…</span>
            </li>
          ) : null}
          {hierarchyLive && hierarchySearch.isError ? (
            <li className="w7-ws-member-suggest-status" role="presentation">
              Could not load members — check hierarchy on :3910.
            </li>
          ) : null}
          {suggestions.map((name) => (
            <li key={name} role="presentation">
              <button
                type="button"
                role="option"
                className="w7-ws-member-suggest-option"
                onMouseDown={(e) => {
                  e.preventDefault()
                  pick(name)
                }}
              >
                {name}
              </button>
            </li>
          ))}
          {!hierarchySearch.isFetching && suggestions.length === 0 && !hierarchySearch.isError ? (
            <li className="w7-ws-member-suggest-status" role="presentation">
              No members match “{draft.trim() || '…'}”.
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  )
}
